/* Field names map to the /tools/ai-generate contract: business, industry, audience, offer, location, goal, notes. */

export const TAB_GUIDE = {
  visibility: {
    title: 'Website visibility check',
    purpose: 'See what local customers and search engines find on your website before you spend money on marketing.',
    steps: ['Enter your website and the area you serve', 'Run the scan', 'Fix the missing signals it reports'],
  },
  ai: {
    title: 'AI Studio',
    purpose: 'Turn verified business details into drafts you can edit, save, and publish.',
    steps: ['Pick the tool that matches your task', 'Answer the questions for that tool', 'Review the draft and replace anything unverified'],
  },
  rank: {
    title: 'Rank tracking',
    purpose: 'Record where you appear in Maps and Search for the keywords customers actually use.',
    steps: ['Add a keyword and the market it matters in', 'Enter the positions you see today', 'Recheck regularly to see the trend'],
  },
  leads: {
    title: 'Lead pipeline',
    purpose: 'Keep every inquiry in one place and move it from new to won.',
    steps: ['Add each inquiry as it arrives', 'Record how to reach them and what they need', 'Update the stage as the conversation moves'],
  },
  operations: {
    title: 'Operations',
    purpose: 'Run the growth modules included in your plan as tracked work with an owner and a due date.',
    steps: ['Choose a module your plan includes', 'Create the work item', 'Move it through to complete'],
  },
  optimize: {
    title: 'Account setup',
    purpose: 'Connect your accounts and finish setup so tracking, reviews, and campaigns report correctly.',
    steps: ['Confirm your business details', 'Complete each connection', 'Save so the workspace uses the right business'],
  },
  activity: {
    title: 'Admin activity',
    purpose: 'Review scans, rank tracking, and lead activity across every account.',
    steps: ['Check recent scan volume', 'Review account tool usage', 'Follow up where activity stalls'],
  },
};

const TONE_FIELD = {
  name: 'tone', label: 'Tone', type: 'select',
  options: ['clear and confident', 'warm and conversational', 'direct and practical', 'premium and polished'],
};

const businessField = (label = 'Business name', placeholder = 'Acme Home Services') => ({ name: 'business', label, placeholder, required: true });

export const AI_TOOL_GUIDE = {
  website_builder: {
    useCase: 'Use this when you need a complete, editable website you can preview and export.',
    outputs: ['Hero and offer', 'Services', 'Trust and proof', 'Lead capture'],
    fields: [
      businessField(),
      { name: 'industry', label: 'What you do', placeholder: 'HVAC repair and installation' },
      { name: 'audience', label: 'Who the site is for', placeholder: 'Homeowners with aging systems' },
      { name: 'location', label: 'Service area', placeholder: 'Orlando, FL' },
      { name: 'offer', label: 'Main offer', placeholder: 'Same-day estimates' },
      { name: 'goal', label: 'What a visitor should do', placeholder: 'Request a quote' },
      TONE_FIELD,
      { name: 'notes', label: 'Services, proof, and anything that must appear', type: 'textarea', placeholder: 'Services you sell, years in business, licenses, service hours' },
    ],
  },
  seo_page_writer: {
    useCase: 'Use this when you want one service page to rank for a specific city or neighborhood.',
    outputs: ['Search intent', 'Page outline', 'Local proof', 'Calls to action'],
    fields: [
      businessField(),
      { name: 'industry', label: 'Service this page covers', placeholder: 'Emergency AC repair' },
      { name: 'audience', label: 'Who is searching', placeholder: 'Homeowners with no cooling' },
      { name: 'location', label: 'City or neighborhood to rank in', placeholder: 'Winter Park, FL' },
      { name: 'offer', label: 'Reason to choose you', placeholder: 'Answered 24/7, arrive same day' },
      { name: 'goal', label: 'What this page should produce', placeholder: 'Calls from emergency searches' },
      TONE_FIELD,
      { name: 'notes', label: 'Keywords, service details, and local proof', type: 'textarea', placeholder: 'Search terms you want to win, neighborhoods served, local landmarks' },
    ],
  },
  poster_creator: {
    useCase: 'Use this when you are promoting one offer or event on a printed or digital poster.',
    outputs: ['Creative direction', 'Primary message', 'Design notes', 'Distribution'],
    fields: [
      businessField(),
      { name: 'industry', label: 'Type of business', placeholder: 'Coffee shop' },
      { name: 'audience', label: 'Who should notice this', placeholder: 'Morning commuters' },
      { name: 'location', label: 'Where it will be seen', placeholder: 'Downtown storefront windows' },
      { name: 'offer', label: 'Offer or event to promote', placeholder: 'Buy one, get one before 9am', required: true },
      { name: 'goal', label: 'What people should do', placeholder: 'Scan the code and claim it' },
      TONE_FIELD,
      { name: 'notes', label: 'Dates, disclaimers, and brand details', type: 'textarea', placeholder: 'Start and end dates, fine print, brand colors, logo rules' },
    ],
  },
  review_responder: {
    useCase: 'Use this when you need to reply to a specific review without sounding defensive or automated.',
    outputs: ['Public response', 'Tone check', 'Follow-up', 'Reputation note'],
    fields: [
      businessField(),
      { name: 'audience', label: 'Reviewer name', placeholder: 'Dana M.' },
      { name: 'offer', label: 'Service they received', placeholder: 'Water heater replacement' },
      { name: 'goal', label: 'Outcome you want', placeholder: 'Resolve it and invite them back' },
      TONE_FIELD,
      {
        name: 'notes', label: 'Paste the review', type: 'textarea', required: true,
        placeholder: 'Paste the exact review text, including the star rating',
        help: 'Paste the real review. Nothing is invented on your behalf, so facts stay accurate.',
      },
    ],
  },
  digital_card_copy: {
    useCase: 'Use this when you need short, memorable copy for a digital business card.',
    outputs: ['Positioning line', 'Short biography', 'Contact actions', 'Share message'],
    fields: [
      businessField(),
      { name: 'industry', label: 'Role and industry', placeholder: 'Owner · Roofing' },
      { name: 'audience', label: 'Who receives this card', placeholder: 'Homeowners after an estimate' },
      { name: 'location', label: 'Market you serve', placeholder: 'Central Florida' },
      { name: 'offer', label: 'What you want to be known for', placeholder: 'Honest inspections, no pressure' },
      { name: 'goal', label: 'What should happen after they scan', placeholder: 'Save the contact and book a call' },
      TONE_FIELD,
      { name: 'notes', label: 'Credentials and links to include', type: 'textarea', placeholder: 'Years in business, licenses, booking link, review link' },
    ],
  },
  campaign_writer: {
    useCase: 'Use this when one offer needs matching email, text, and a conversion path.',
    outputs: ['Campaign angle', 'Email', 'Text message', 'Conversion path'],
    fields: [
      businessField(),
      { name: 'industry', label: 'Industry', placeholder: 'Pest control' },
      { name: 'audience', label: 'Who this campaign targets', placeholder: 'Past customers from last season' },
      { name: 'location', label: 'Market', placeholder: 'Tampa, FL' },
      { name: 'offer', label: 'Campaign offer', placeholder: '$99 seasonal treatment', required: true },
      { name: 'goal', label: 'Campaign goal', placeholder: 'Book 20 treatments this month' },
      TONE_FIELD,
      { name: 'notes', label: 'Dates, exclusions, and links', type: 'textarea', placeholder: 'Run dates, who to exclude, booking link, required disclaimers' },
    ],
  },
  social_planner: {
    useCase: 'Use this when you need a realistic posting plan instead of one-off posts.',
    outputs: ['Weekly theme', 'Post ideas', 'Calls to action', 'Publishing rhythm'],
    fields: [
      businessField(),
      { name: 'industry', label: 'Industry', placeholder: 'Landscaping' },
      { name: 'audience', label: 'Who you want to reach', placeholder: 'Homeowners planning spring projects' },
      { name: 'location', label: 'Market', placeholder: 'Orlando, FL' },
      { name: 'offer', label: 'What to feature this month', placeholder: 'Spring cleanup packages' },
      { name: 'goal', label: 'Goal for this month', placeholder: 'Fill the spring schedule' },
      TONE_FIELD,
      { name: 'notes', label: 'Platforms, posting days, and seasonal topics', type: 'textarea', placeholder: 'Where you post, how often you can realistically publish, upcoming events' },
    ],
  },
  followup_writer: {
    useCase: 'Use this when leads go quiet after the first conversation.',
    outputs: ['Immediate reply', 'Day two follow-up', 'Final check-in', 'Handoff rule'],
    fields: [
      businessField(),
      { name: 'industry', label: 'Service requested', placeholder: 'Roof replacement' },
      { name: 'audience', label: 'Type of lead', placeholder: 'Homeowner who requested a quote' },
      { name: 'location', label: 'Market', placeholder: 'Jacksonville, FL' },
      { name: 'offer', label: 'What you can offer now', placeholder: 'Free on-site estimate this week' },
      { name: 'goal', label: 'What you want them to do', placeholder: 'Confirm an appointment time' },
      TONE_FIELD,
      { name: 'notes', label: 'How the lead arrived and what they said', type: 'textarea', placeholder: 'Source, timing, budget concerns, objections already raised' },
    ],
  },
  appointment_script: {
    useCase: 'Use this when booked appointments are being missed or rescheduled.',
    outputs: ['Booking message', 'Confirmation', 'Reminder', 'No-show recovery'],
    fields: [
      businessField(),
      { name: 'industry', label: 'Appointment type', placeholder: 'In-home estimate' },
      { name: 'audience', label: 'Who is booking', placeholder: 'Homeowners scheduling after hours' },
      { name: 'location', label: 'Where the appointment happens', placeholder: 'At the customer address' },
      { name: 'offer', label: 'What the appointment includes', placeholder: '60-minute inspection, written quote' },
      { name: 'goal', label: 'Booking goal', placeholder: 'Reduce no-shows' },
      TONE_FIELD,
      { name: 'notes', label: 'Scheduling window and policies', type: 'textarea', placeholder: 'Available times, cancellation policy, arrival window, what to prepare' },
    ],
  },
  workflow_builder: {
    useCase: 'Use this when the same customer situation is handled differently every time.',
    outputs: ['Trigger', 'Conditions', 'Actions', 'Team escalation'],
    fields: [
      businessField(),
      { name: 'industry', label: 'Workflow area', placeholder: 'New lead response' },
      { name: 'audience', label: 'Who this affects', placeholder: 'Office staff and two technicians' },
      { name: 'location', label: 'Locations covered', placeholder: 'Both service locations' },
      { name: 'offer', label: 'Promise to the customer', placeholder: 'A reply within 15 minutes' },
      { name: 'goal', label: 'What the workflow should accomplish', placeholder: 'No lead waits without an owner' },
      TONE_FIELD,
      { name: 'notes', label: 'Trigger, owners, and response targets', type: 'textarea', placeholder: 'What starts it, who owns each step, hours of operation, escalation timing' },
    ],
  },
  lead_qualifier: {
    useCase: 'Use this when your team spends time on leads that were never a fit.',
    outputs: ['Qualification criteria', 'Questions', 'Scoring model', 'Routing rule'],
    fields: [
      businessField(),
      { name: 'industry', label: 'Services you qualify for', placeholder: 'Full system replacement, repairs' },
      { name: 'audience', label: 'Your ideal customer', placeholder: 'Homeowners, not renters' },
      { name: 'location', label: 'Service area you accept', placeholder: 'Within 30 miles of Orlando' },
      { name: 'offer', label: 'Best-fit job or project', placeholder: 'Replacement over $6,000' },
      { name: 'goal', label: 'What a qualified lead looks like', placeholder: 'Ready to decide within 30 days' },
      TONE_FIELD,
      { name: 'notes', label: 'Budget range, jobs you decline, and owners', type: 'textarea', placeholder: 'Minimum job size, work you turn down, who receives each type of lead' },
    ],
  },
  intake_builder: {
    useCase: 'Use this when your team starts jobs without the details they need.',
    outputs: ['Contact fields', 'Service questions', 'Qualification logic', 'Consent and handoff'],
    fields: [
      businessField(),
      { name: 'industry', label: 'Service being booked', placeholder: 'Kitchen remodel consultation' },
      { name: 'audience', label: 'Who fills this out', placeholder: 'Homeowners requesting a quote' },
      { name: 'location', label: 'Market', placeholder: 'Orlando, FL' },
      { name: 'offer', label: 'What happens after they submit', placeholder: 'A scheduled call within 24 hours' },
      { name: 'goal', label: 'What the team needs before service', placeholder: 'Scope, access, and timing confirmed' },
      TONE_FIELD,
      { name: 'notes', label: 'Required details and consent', type: 'textarea', placeholder: 'Must-have questions, photos or measurements, text and email consent' },
    ],
  },
  revenue_recovery: {
    useCase: 'Use this when quoted or past customers stopped responding.',
    outputs: ['Audience segment', 'Recovery offer', 'Message sequence', 'Success measures'],
    fields: [
      businessField(),
      { name: 'industry', label: 'Industry', placeholder: 'Dental' },
      { name: 'audience', label: 'Who to reactivate', placeholder: 'Quoted but never booked, last 90 days' },
      { name: 'location', label: 'Markets included', placeholder: 'Orlando and Kissimmee' },
      { name: 'offer', label: 'Recovery offer', placeholder: 'Honor the original quote for 14 days' },
      { name: 'goal', label: 'Recovery goal', placeholder: 'Rebook 15 stalled estimates' },
      TONE_FIELD,
      { name: 'notes', label: 'Time since contact and past objections', type: 'textarea', placeholder: 'How long they have been quiet, why they hesitated, what changed since' },
    ],
  },
  funnel_auditor: {
    useCase: 'Use this when you get leads but cannot tell where they are lost.',
    outputs: ['Acquisition', 'Response speed', 'Sales conversion', 'Retention'],
    fields: [
      businessField(),
      { name: 'industry', label: 'Industry', placeholder: 'Plumbing' },
      { name: 'audience', label: 'Customer type to audit', placeholder: 'Residential service calls' },
      { name: 'location', label: 'Markets to review', placeholder: 'Orlando metro' },
      { name: 'offer', label: 'Main service in this funnel', placeholder: 'Repair and replacement quotes' },
      { name: 'goal', label: 'What you want to fix', placeholder: 'Stop losing quoted jobs' },
      TONE_FIELD,
      {
        name: 'notes', label: 'Numbers you already know', type: 'textarea',
        placeholder: 'Monthly leads, typical response time, quote-to-close rate, main sources',
        help: 'Only your real numbers are used. Missing figures are called out instead of estimated.',
      },
    ],
  },
  location_playbook: {
    useCase: 'Use this when several locations need one coordinated plan.',
    outputs: ['Market priorities', 'Location scorecard', 'Campaign rollout', 'Management cadence'],
    fields: [
      businessField(),
      { name: 'industry', label: 'Industry', placeholder: 'Auto repair' },
      { name: 'audience', label: 'Customers across these markets', placeholder: 'Commuters and fleet accounts' },
      { name: 'location', label: 'Markets to coordinate', placeholder: 'Orlando, Tampa, Miami', required: true },
      { name: 'offer', label: 'Service to prioritize', placeholder: 'Fleet maintenance contracts' },
      { name: 'goal', label: 'Goal across every market', placeholder: 'Even lead volume by location' },
      TONE_FIELD,
      { name: 'notes', label: 'Team structure and differences by location', type: 'textarea', placeholder: 'Who manages each location, staffing gaps, which markets underperform' },
    ],
  },
};

export const aiToolGuide = toolId => AI_TOOL_GUIDE[toolId] || AI_TOOL_GUIDE.website_builder;
